import React, { useState } from "react";
import PropTypes from "prop-types";
import { Close, Chevron } from "./RxIcons";

import "../styles/prescriptionCards.scss";

/**
 * The five picker cards of the review-prescription builder — Diagnosis, Chief
 * complaints, Reports, Medicine, Other investigations. Source: Figma "Screen /
 * review prescription" (1762:15386) → left builder Cards (1762:16353).
 *
 * One component for all five because their anatomy is identical: header + a
 * "N selected" pill + collapse chevron, then per group a grey caption with the
 * group's row count and its rows. Only the row payload differs — the ICD chip,
 * the "NEW TODAY" / "UPDATED TODAY" pill and the sub-line are all optional per
 * row.
 *
 * Presentational: `selected`/`onToggle` live in the screen's state owner, since
 * ticking a row is what redraws the document preview. Collapse is local UI; a card
 * starts open unless the host passes `defaultOpen={false}`.
 *
 * This module also owns the one `prescriptionCards.scss` import — every left
 * pane renders picker cards, so the sheet loads exactly once from here.
 */

/** The tick shown once a row is checked; the box itself is drawn in CSS. */
const Tick = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
    <path d="M3.5 8.4l3 3 6-6.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The app's own pencil — PatientHeaderCard.js:41, the same path verbatim, so a
 * row's edit control is the pencil this app already draws rather than a second
 * one. Filled, but the path describes an OUTLINED pencil, which is what the
 * board shows.
 */
const Pencil = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M3.3332 12.6665H4.1742L10.9985 5.8422L10.1575 5.0012L3.3332 11.8255V12.6665ZM2.3332 13.6665V11.4102L11.1269 2.62037C11.2276 2.52881 11.3389 2.45809 11.4607 2.4082C11.5826 2.3582 11.7104 2.3332 11.844 2.3332C11.9777 2.3332 12.1071 2.35693 12.2324 2.40437C12.3577 2.45181 12.4686 2.52726 12.5652 2.6307L13.3794 3.45504C13.4828 3.55159 13.5565 3.6627 13.6005 3.78837C13.6445 3.91404 13.6665 4.0397 13.6665 4.16537C13.6665 4.29948 13.6436 4.42743 13.5979 4.5492C13.5521 4.67109 13.4793 4.78243 13.3794 4.8832L4.58954 13.6665H2.3332ZM10.5707 5.42904L10.1575 5.0012L10.9985 5.8422L10.5707 5.42904Z" />
  </svg>
);

/** The header add button's glyph — 11x11 in Figma, stroke inherits the label. */
const Plus = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M6 1.5v9M1.5 6h9" strokeLinecap="round" />
  </svg>
);

const rowShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isNew: PropTypes.bool,
  /** Updated (not added) by this doctor in this consultation — its own badge. */
  isUpdated: PropTypes.bool,
});

/**
 * A single pickable row. The whole row is the `<label>`, so label text and
 * checkbox are one hit target and the real (visually replaced) input keeps the
 * accessible name + space-to-toggle for free.
 *
 * `locked` rows (Figma 3031:9686 "Checkbox · locked (not toggleable)") are
 * ticked and stay ticked — unticking is not the way to drop them, `onRemove`
 * is, where the host offers it (an "Updated today" medicine gets none: it
 * stays in). The <label> wrapper is dropped for those, because a label whose
 * control ignores clicks still LOOKS clickable across its whole width.
 */
const Row = ({ row, checked, locked, onToggle, onRemove, onEdit }) => {
  const body = (
    <>
      <span className={`rx-cb${locked ? " is-locked" : ""}`} aria-hidden="true">
        <Tick />
      </span>
      <span className="rx-row-body">
        <span className="rx-row-line">
          <span className="rx-row-label">{row.label}</span>
          {row.isNew ? (
            <span className="rx-new">New today</span>
          ) : row.isUpdated ? (
            <span className="rx-new is-updated">Updated today</span>
          ) : null}
        </span>
      </span>
    </>
  );

  return (
    <li className="rx-row">
      {locked ? (
        <div className="rx-row-main is-locked">
          {/* aria-disabled, not `disabled`: a disabled input drops out of the
              tab order and loses its accessible name, so a screen reader would
              never announce that the row is included at all. */}
          <input
            type="checkbox"
            className="rx-cb-input"
            checked
            readOnly
            aria-disabled="true"
            aria-label={`${row.label} — included${onRemove ? ", remove to take it out" : ""}`}
          />
          {body}
        </div>
      ) : (
        <label className="rx-row-main">
          <input
            type="checkbox"
            className="rx-cb-input"
            checked={checked}
            onChange={() => onToggle(row.id)}
          />
          {body}
        </label>
      )}

      {onRemove ? (
        <button
          type="button"
          className="rx-row-x"
          aria-label={`Remove ${row.label}`}
          onClick={() => onRemove(row)}
        >
          <Close />
        </button>
      ) : null}

      {/* OUTSIDE the <label>, like the × above it — but NOT because a button
          inside one would toggle the checkbox. It would not: a label's
          activation behaviour is skipped when the click lands on interactive
          content, which a <button> is. That was measured, not assumed (see
          scripts/verify/rx-row-edit.js).

          The reasons it sits out here are the ordinary ones. The label carries
          `cursor: pointer` and a hover fill across its whole box, which says
          "this toggles" — and the pencil does not. And the row's layout puts
          the pencil at its right edge, under the ×, which needs the two to be
          siblings of the label rather than inside it.

          Rendered only where the host passes `onEdit` — desktop passes nothing
          and this is not in its DOM at all. Last in the row, so tab order
          follows what the board draws: the name, then remove, then edit. */}
      {onEdit ? (
        <button
          type="button"
          className="rx-row-edit"
          aria-label={`Edit ${row.label}`}
          onClick={() => onEdit(row)}
        >
          <Pencil />
        </button>
      ) : null}
    </li>
  );
};
Row.propTypes = {
  row: rowShape.isRequired,
  checked: PropTypes.bool.isRequired,
  locked: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  /** Present only where the design draws an ×; omit to render no control. */
  onRemove: PropTypes.func,
  /** Called with the row when its pencil is clicked. Omit to render no pencil. */
  onEdit: PropTypes.func,
};

const RxPickerCard = ({
  title,
  groups,
  selected,
  locked,
  removable,
  onToggle,
  onRemove,
  onEdit,
  addLabel,
  onAdd,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  // "N selected" counts THIS card's rows only — `selected` is the card's own
  // id list, but flattening the groups keeps that honest either way.
  const rows = groups.reduce((all, g) => all.concat(g.rows), []);
  const count = rows.filter((r) => selected.indexOf(r.id) !== -1).length;

  return (
    <section className="rx-card">
      <header className="rx-card-head">
        <h3 className="rx-card-title">{title}</h3>
        <div className="rx-card-head-end">
          {/* The word is wrapped so a narrow layout can drop it and keep the
              number. Desktop renders exactly as before — an inline span around
              text that was already inline. */}
          {count > 0 ? (
            <span className="rx-selected-pill">
              {count}
              <span className="rx-selected-word"> selected</span>
            </span>
          ) : null}
          {/* Figma 3037:13226 et al — sits between the title and the chevron.
              Rendered only when the host supplies BOTH, which is how the
              advised-investigations card stays button-free (the design gives it
              a search field instead). */}
          {addLabel && onAdd ? (
            <button type="button" className="rx-add" onClick={onAdd}>
              <Plus />
              {addLabel}
            </button>
          ) : null}
          <button
            type="button"
            className={`rx-collapse${open ? "" : " is-collapsed"}`}
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${title}`}
            onClick={() => setOpen((o) => !o)}
          >
            <Chevron />
          </button>
        </div>
      </header>

      {open ? (
        <div className="rx-card-body">
          {groups.map((g) => (
            <div className="rx-group" key={g.label}>
              <p className="rx-group-cap">
                {g.label}
                <span className="rx-group-n">{g.rows.length}</span>
              </p>
              <ul className="rx-rows">
                {g.rows.map((r) => (
                  <Row
                    key={r.id}
                    row={r}
                    checked={selected.indexOf(r.id) !== -1}
                    locked={(locked || []).indexOf(r.id) !== -1}
                    onToggle={onToggle}
                    // Figma draws the × on the "added in this consultation"
                    // rows only — i.e. the locked ones, which have no other way
                    // out. `removable` narrows that further: a row the host
                    // cannot actually delete (no backing record) gets no ×
                    // rather than a button that fails.
                    onRemove={
                      onRemove && (removable || []).indexOf(r.id) !== -1 ? onRemove : undefined
                    }
                    onEdit={onEdit}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};
RxPickerCard.propTypes = {
  title: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      rows: PropTypes.arrayOf(rowShape).isRequired,
    })
  ).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Row ids that are ticked and cannot be un-ticked. */
  locked: PropTypes.arrayOf(PropTypes.string),
  /** Row ids that may be removed — these are the ones that render an ×. */
  removable: PropTypes.arrayOf(PropTypes.string),
  onToggle: PropTypes.func.isRequired,
  /** Called with the row when its × is clicked. Omit for cards with no ×. */
  onRemove: PropTypes.func,
  /**
   * Called with the row when its pencil is clicked. Omit — as every desktop
   * caller does — and no row renders one.
   */
  onEdit: PropTypes.func,
  /** Header add button. Both or neither — one alone renders nothing. */
  addLabel: PropTypes.string,
  onAdd: PropTypes.func,
  /** Whether the card starts expanded. Only the first render reads it. */
  defaultOpen: PropTypes.bool,
};

export default RxPickerCard;
